import { useState, useEffect, useCallback, useMemo } from 'react';
import { formatBRL } from '@caixa/lib/format';
import type { ValoresPagamento, VendaAjustesSnapshot } from '@caixa/types';

export type PaymentChoice = 'pix' | 'dinheiro' | 'credito' | 'debito' | 'dividir' | 'desperdicios';

const CHOICES: { key: PaymentChoice; label: string }[] = [
  { key: 'pix', label: 'PIX' },
  { key: 'dinheiro', label: 'Dinheiro' },
  { key: 'credito', label: 'Crédito' },
  { key: 'debito', label: 'Débito' },
  { key: 'dividir', label: 'Dividir' },
  { key: 'desperdicios', label: 'Desperdícios' },
];

function roundCentavos(n: number): number {
  return Math.round(n * 100) / 100;
}

interface PaymentChoiceModalProps {
  open: boolean;
  total: number;
  onConfirm: (valores: ValoresPagamento, totalComDesconto: number, ajustes: VendaAjustesSnapshot) => void | Promise<void>;
  onClose: () => void;
  onOpenSplit: (totalComDesconto: number, ajustes: VendaAjustesSnapshot) => void;
  onOpenCash: (totalComDesconto: number, ajustes: VendaAjustesSnapshot) => void;
  onOpenDesperdicios: () => void;
}

export function PaymentChoiceModal({
  open,
  total,
  onConfirm,
  onClose,
  onOpenSplit,
  onOpenCash,
  onOpenDesperdicios,
}: PaymentChoiceModalProps) {
  const [selected, setSelected] = useState<PaymentChoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [descontoStr, setDescontoStr] = useState('');
  const [acrescimoStr, setAcrescimoStr] = useState('');

  const descontoNum = useMemo(() => {
    if (descontoStr.trim() === '') return 0;
    const n = parseFloat(descontoStr.replace(',', '.'));
    if (Number.isNaN(n) || n < 0) return 0;
    if (n > 100) return 100;
    return n;
  }, [descontoStr]);

  const acrescimoNum = useMemo(() => {
    if (acrescimoStr.trim() === '') return 0;
    const n = parseFloat(acrescimoStr.replace(',', '.'));
    if (Number.isNaN(n) || n < 0) return 0;
    return n;
  }, [acrescimoStr]);

  const totalFinal = useMemo(
    () => roundCentavos(total * (1 - descontoNum / 100) * (1 + acrescimoNum / 100)),
    [total, descontoNum, acrescimoNum]
  );
  const ajustes = useMemo<VendaAjustesSnapshot>(() => {
    const aposDesconto = total * (1 - descontoNum / 100);
    return {
      subtotalBase: total,
      descontoPercentual: descontoNum,
      descontoValor: roundCentavos(total - aposDesconto),
      acrescimoPercentual: acrescimoNum,
      acrescimoValor: roundCentavos(totalFinal - aposDesconto),
    };
  }, [total, descontoNum, acrescimoNum, totalFinal]);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setDescontoStr('');
      setAcrescimoStr('');
    }
    setLoading(false);
  }, [open]);

  const handleDescontoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(',', '.');
    if (raw === '' || raw === '-') {
      setDescontoStr('');
      return;
    }
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return;
    const clamped = Math.min(100, Math.max(0, n));
    setDescontoStr(String(clamped));
  }, []);

  const handleAcrescimoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(',', '.');
    if (raw === '' || raw === '-') {
      setAcrescimoStr('');
      return;
    }
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return;
    setAcrescimoStr(String(Math.max(0, n)));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selected) return;
    if (selected === 'dividir') {
      onOpenSplit(totalFinal, ajustes);
      return;
    }
    if (selected === 'dinheiro') {
      onOpenCash(totalFinal, ajustes);
      return;
    }
    if (selected === 'desperdicios') {
      onOpenDesperdicios();
      return;
    }
    setLoading(true);
    const zero: ValoresPagamento = { pix: 0, dinheiro: 0, credito: 0, debito: 0 };
    const valores: ValoresPagamento = { ...zero, [selected]: totalFinal };
    try {
      await onConfirm(valores, totalFinal, ajustes);
    } catch {
      /* erro exibido pelo handler pai */
    } finally {
      setLoading(false);
    }
  }, [selected, totalFinal, ajustes, onConfirm, onOpenSplit, onOpenCash, onOpenDesperdicios]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Enter' && selected) {
        e.preventDefault();
        void handleConfirm();
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, selected, onClose, handleConfirm]);

  if (!open) return null;

  return (
    <div
      className="caixa-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-choice-title"
    >
      <div className="caixa-modal caixa-modal--md caixa-modal--stack">
        <h2 id="payment-choice-title" className="sr-only">
          Escolha a forma de pagamento
        </h2>
        <div className="caixa-modal-section--head" style={{ display: "block" }}>
          <div className="flex flex-wrap items-center gap-2">
            <label className="caixa-label mb-0">Desconto %</label>
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={descontoStr}
              onChange={handleDescontoChange}
              placeholder="0"
              className="caixa-input caixa-input--333 w-16"
              aria-label="Percentual de desconto (0 a 100)"
            />
            <label className="caixa-label mb-0">Acréscimo %</label>
            <input
              type="number"
              min={0}
              step={1}
              value={acrescimoStr}
              onChange={handleAcrescimoChange}
              placeholder="0"
              className="caixa-input caixa-input--333 w-16"
              aria-label="Percentual de acréscimo"
            />
          </div>
          <div className="mt-2">
            <span className="caixa-label">Valor total</span>
            <div className="text-2xl font-bold text-[#f5f5f5] mt-1">{formatBRL(totalFinal)}</div>
          </div>
        </div>
        <div className="caixa-modal-section--body grid grid-cols-2 sm:grid-cols-3 gap-2">
          {CHOICES.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setSelected(key)}
              className={`caixa-choice ${selected === key ? "caixa-choice--on" : ""}`}
              style={selected === key ? undefined : { backgroundColor: "#333333" }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="caixa-modal-section--foot">
          <button
            type="button"
            onClick={onClose}
            className="caixa-btn-outline"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selected || loading}
            className="caixa-btn-success"
          >
            {loading ? 'Finalizando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}
